import React, { useState } from 'react';
import _round from 'lodash/round';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faGift } from '@fortawesome/pro-light-svg-icons';

import { BarCode, PhysicalCard, Reload as ReloadIcon } from '../../../components/custom-icons';

import { ProcessingBuyAgain, sumByLineItemMoneyProp } from './RecentPurchase';

import { ProductType } from 'common/api/e-comm/models/Product';
import { Order, OrderLineItem } from 'common/api/e-comm/models/Order';
import { monetizeWithDecimal } from 'common/utils/strings';
import { createStyles } from 'utils/createStyle';
import { PaymentType } from 'common/api/e-comm/payment-types';
import { FeatureFlag } from 'components/feature-flag';
import { LinkBtn } from 'components/link-btn';
import { Modal, ModalSize } from 'components/modal/Modal';
import { Btn } from 'components/btn/Btn';

const styles = createStyles({
    buyAgainButton: ['w-full text-white bg-brand', { paddingTop: 10, paddingBottom: 10 }],
    detailTotalEarnings: [
        'text-lg md:text-xl text-success text-center md:border-none border-b border-grey-light border-solid md:pb-4 md:pt-4 pb-5 pt-5',
    ],
    detailHeaderContainer: [
        'hidden md:grid grid-cols-6 border-solid border-b border-grey-light',
        { paddingRight: 20, paddingLeft: 20 },
    ],
    detailColumnHeader: [
        'text-brand-dark text-center',
        { fontSize: 12, lineHeight: '16px', fontWeight: 600, paddingBottom: 7 },
    ],
    detailBrandImage: [
        { maxHeight: 40, maxWidth: 40, paddingTop: 7.5, paddingBottom: 7.5, marginRight: 12.5 },
    ],
    mobileBrandImage: [{ maxWidth: 35 }],
    localProductMobile: [{ minWidth: 35 }],
    localProduct: [{ minWidth: 40, minHeight: 40, marginRight: 12.5 }],
    detailBrandName: [
        'text-brand-dark font-bold truncate w-full',
        { fontSize: 14, lineHeight: '19px' },
    ],
    detailCardType: [
        'text-grey-1 truncate ',
        { fontSize: 12, lineHeight: '14px', fontWeight: 400 },
    ],
    detailLineItemContainer: [
        'border-b border-solid border-grey-light md:pl-5 md:pr-5 pl-0 pr-0 pb-3 mt-3',
    ],
    detailLineItem: ['hidden md:grid grid-cols-6 flex flex-row items-center', { minHeight: 60 }],
    mobileLineItem: [
        'grid md:hidden grid-cols-6 text-grey-1 grid-auto-rows',
        { fontSize: 12, lineHeight: '14px', fontWeight: 400, padding: '5px 10px' },
    ],
    detailLineItemText: [
        'text-grey-1 text-center',
        { fontSize: 14, lineHeight: '19px', fontWeight: 400 },
    ],
    detailEarningPercent: ['text-grey-1', { fontSize: 12, lineHeight: '14px', weight: 400 }],
    detailEarningAmount: ['text-success font-bold'],
    detailValueAmount: [
        'text-brand-dark font-bold text-center',
        { fontSize: 14, lineHeight: '19px' },
    ],
    link: [{ fontSize: 12, lineHeight: '16px', fontWeight: 600, textDecoration: 'underline' }],
    mobileLineItemAmount: [
        'text-brand-dark',
        { fontSize: 14, lineHeight: '19px', fontWeight: 400 },
    ],
    detailFooter: [
        'text-grey-1 grid grid-cols-6',
        {
            padding: '10px 20px',
            fontSize: 14,
            lineHeight: '19px',
            fontWeight: 400,
            rowGap: 5,
        },
    ],
    detailShippingInfo: [
        'col-span-6 list-disc',
        { paddingLeft: 25, fontSize: 12, lineHeight: '14px' },
    ],
    detailShippingLineItems: [
        {
            '&:first-child': { paddingTop: 0 },
            '&last-child': { paddingBottom: 0 },
            paddingTop: 2.5,
            paddingBottom: 2.5,
        },
    ],
    detailTotalDivider: [
        'grid grid-cols-6 border-t border-b border-grey-light border-solid text-brand-dark flex flex-row items-center bg-grey-accent',
        { padding: '5px 20px' },
    ],
    detailTotalLabel: ['font-semibold text-center', { fontSize: 12, lineHeight: '16px' }],
    detailTotalAmount: [
        'font-bold col-span-2 md:col-span-1 text-right md:text-center md:text-sm text-lg',
    ],
    detailFooterClose: ['cursor-pointer text-center', { paddingTop: 10, paddingBottom: 10 }],
});

const calculateTotalQuantity = (lineItems: OrderLineItem[]) => {
    return lineItems.reduce((sum, lineItem) => sum + lineItem.quantity, 0);
};

const calculateEarnPercent = (lineItem: OrderLineItem): string => {
    const percent = (lineItem.totalEarnings / lineItem.totalValue) * 100;
    return `${_round(percent, 2).toFixed(2)}%`;
};

const calculateOrderTotal = (order: Order): string => {
    const lineItemTotal = order.lineItems.reduce((sum, lineItem) => sum + lineItem.totalValue, 0);
    const shippingCost = order.shippingInformation?.cost || 0;

    const total = lineItemTotal + order.orderFees + shippingCost;
    return monetizeWithDecimal(total);
};

const determineCardType = (order: Order, lineItem: OrderLineItem): string => {
    if (lineItem.productType === ProductType.Reload) {
        return 'Reload';
    }

    if (lineItem.productType === ProductType.EGiftCard) {
        return 'eGift';
    }

    if (order.isShipToHome) {
        return 'Physical - Ship to home';
    }

    return 'Physical - Ship to coordinator';
};

const determineCardIcon = (order: Order, lineItem: OrderLineItem) => {
    const cardType = determineCardType(order, lineItem);

    switch (cardType) {
        case 'Reload':
            return <ReloadIcon height="15px" style={{ minHeight: 15, minWidth: 16.66 }} />;
        case 'eGift':
            return <BarCode height="15px" style={{ minHeight: 15, minWidth: 23.33 }} />;
        case 'Physical - Ship to home':
        case 'Physical - Ship to coordinator':
        default:
            return <PhysicalCard height="15px" style={{ minHeight: 15, minWidth: 17.72 }} />;
    }
};

const orderFeeLabel = (order: Order) => {
    switch (order.paymentType) {
        case PaymentType.BankAccount:
        case PaymentType.CreditCard:
            return 'Convenience Fee';
        default:
            return null;
    }
};

interface RecentPurchaseDetailProps {
    order: Order;
    expand: boolean;
    setExpand: (expand: boolean) => void;
    getBrandSrcUrl: (brandId: number) => string;
    onBuyAgain: () => void;
    isBuyAgainRunning: boolean;
}

export const RecentPurchaseDetail = ({
    order,
    expand,
    setExpand,
    getBrandSrcUrl,
    onBuyAgain,
    isBuyAgainRunning,
}: RecentPurchaseDetailProps) => {
    const totalEarnings = sumByLineItemMoneyProp(order.lineItems, 'totalEarnings');

    const [giftDetailsModalInfo, setGiftDetailsModalInfo] = useState<OrderLineItem>();

    const showGiftDetails = (lineItem: OrderLineItem) => {
        setGiftDetailsModalInfo(lineItem);
    };

    const hideGiftDetailsModal = () => {
        setGiftDetailsModalInfo(undefined);
    };

    return (
        <React.Fragment>
            <section className={classNames(expand ? 'block' : 'hidden')}>
                {!isBuyAgainRunning ? (
                    <button
                        className={classNames(styles.link, styles.buyAgainButton)}
                        onClick={onBuyAgain}
                    >
                        Buy Again
                    </button>
                ) : (
                    <ProcessingBuyAgain orientation="horizontal" />
                )}
                <div>
                    <h4 className={classNames(styles.detailTotalEarnings)}>
                        - You earned <span className="font-bold">{totalEarnings}</span> on this
                        order -
                    </h4>
                    <div className={styles.detailHeaderContainer}>
                        <span className={classNames('col-span-2', styles.detailColumnHeader)}>
                            Product
                        </span>
                        <span className={classNames(styles.detailColumnHeader)}>Earnings</span>
                        <span className={classNames(styles.detailColumnHeader)}>Value</span>
                        <span className={classNames(styles.detailColumnHeader)}>Quantity</span>
                        <span className={classNames(styles.detailColumnHeader)}>Dollar Amount</span>
                    </div>
                    <div className={classNames(styles.detailLineItemContainer)}>
                        {order.lineItems.map((lineItem, idx) => {
                            return (
                                <React.Fragment key={`${order.orderNumber}-${idx}`}>
                                    <article className={classNames(styles.mobileLineItem)}>
                                        {!lineItem.isLocalProduct ? (
                                            <img
                                                className={classNames(styles.mobileBrandImage)}
                                                src={getBrandSrcUrl(lineItem.brandId)}
                                            />
                                        ) : (
                                            <span className={styles.localProductMobile}>
                                                <FontAwesomeIcon
                                                    className={classNames('text-brand')}
                                                    icon={faTag}
                                                    size="lg"
                                                />
                                            </span>
                                        )}
                                        <div className="col-span-3 flex flex-col">
                                            <div className="flex flex-row items-center mb-1 w-full">
                                                <span className={styles.detailBrandName}>
                                                    {!lineItem.isLocalProduct
                                                        ? lineItem.brandName
                                                        : 'Local Product'}
                                                </span>
                                            </div>
                                            <FeatureFlag flagKey="enable_custom_card_names">
                                                {lineItem.cardName ? (
                                                    <span
                                                        className={classNames(
                                                            'mb-1',
                                                            styles.detailCardType
                                                        )}
                                                    >
                                                        {`- ${lineItem.cardName}`}
                                                    </span>
                                                ) : null}
                                            </FeatureFlag>
                                            {lineItem.giftDetails ? (
                                                <div className="mb-1">
                                                    <div className="flex flex-row items-center">
                                                        <LinkBtn
                                                            className="text-xs text-brand"
                                                            onClick={() =>
                                                                showGiftDetails(lineItem)
                                                            }
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faGift}
                                                                className="mr-1"
                                                            />
                                                            <span>Gift Details</span>
                                                        </LinkBtn>
                                                    </div>
                                                </div>
                                            ) : null}
                                            <span className="mb-1">
                                                <span className="font-semibold">Earn </span>
                                                {calculateEarnPercent(lineItem)} ={' '}
                                                <span className="text-success">
                                                    {monetizeWithDecimal(lineItem.totalEarnings)}
                                                </span>
                                            </span>
                                            <span className="col-span-2">
                                                <span className="font-semibold">Value </span>
                                                {monetizeWithDecimal(lineItem.faceValue)} x
                                                {lineItem.quantity}
                                            </span>
                                        </div>
                                        <span className="col-span-6" />
                                        <div className="flex flex-row justify-end col-span-6">
                                            <span className={styles.mobileLineItemAmount}>
                                                Amount {monetizeWithDecimal(lineItem.totalValue)}
                                            </span>
                                        </div>
                                    </article>
                                    <article
                                        className={classNames(styles.detailLineItem)}
                                        key={`${order.orderNumber}-${idx}`}
                                    >
                                        <div className="flex flex-row col-span-2 items-center">
                                            {!lineItem.isLocalProduct ? (
                                                <img
                                                    className={styles.detailBrandImage}
                                                    src={getBrandSrcUrl(lineItem.brandId)}
                                                />
                                            ) : (
                                                <span className={styles.localProduct}>
                                                    <FontAwesomeIcon
                                                        className="text-brand"
                                                        icon={faTag}
                                                        size="lg"
                                                    />
                                                </span>
                                            )}
                                            <div className="w-9/12 xl:w-11/12 pr-3">
                                                <div className="flex flex-row items-center">
                                                    <h4 className={styles.detailBrandName}>
                                                        {!lineItem.isLocalProduct
                                                            ? lineItem.brandName
                                                            : 'Local Product'}
                                                    </h4>
                                                </div>
                                                {lineItem.giftDetails ? (
                                                    <div className="mt-1">
                                                        <div className="flex flex-row items-center">
                                                            <LinkBtn
                                                                className="text-sm text-brand"
                                                                onClick={() =>
                                                                    showGiftDetails(lineItem)
                                                                }
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={faGift}
                                                                    className="mr-1"
                                                                />
                                                                <span>Gift Details</span>
                                                            </LinkBtn>
                                                        </div>
                                                        <div className="text-xs text-dark-grey truncate mt-1">
                                                            {lineItem.giftDetails.toName}
                                                        </div>
                                                    </div>
                                                ) : null}
                                                <span
                                                    className={classNames(
                                                        'mt-1.5 block pr-2 pb-1',
                                                        styles.detailCardType
                                                    )}
                                                >
                                                    <FeatureFlag flagKey="enable_custom_card_names">
                                                        {lineItem.cardName
                                                            ? `- ${lineItem.cardName}`
                                                            : ''}
                                                    </FeatureFlag>
                                                </span>
                                            </div>
                                        </div>
                                        <div className="text-center">
                                            <span
                                                className={classNames(styles.detailEarningPercent)}
                                            >
                                                Earn {calculateEarnPercent(lineItem)} =
                                                <span className={styles.detailEarningAmount}>
                                                    {' '}
                                                    {monetizeWithDecimal(lineItem.totalEarnings)}
                                                </span>
                                            </span>
                                        </div>
                                        <span className={classNames(styles.detailLineItemText)}>
                                            {monetizeWithDecimal(lineItem.faceValue)}
                                        </span>
                                        <span className={classNames(styles.detailLineItemText)}>
                                            {lineItem.quantity}
                                        </span>
                                        <span className={classNames(styles.detailValueAmount)}>
                                            {monetizeWithDecimal(lineItem.totalValue)}
                                        </span>
                                    </article>
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
                <div className={classNames(styles.detailFooter)}>
                    <span className="col-span-4">Subtotal</span>
                    <span className="text-center md:visible invisible">
                        {calculateTotalQuantity(order.lineItems)}
                    </span>
                    <span className="text-center text-brand-dark">
                        {sumByLineItemMoneyProp(order.lineItems, 'totalValue')}
                    </span>
                    <span className="col-span-5">{orderFeeLabel(order)}</span>
                    <span className="text-center text-brand-dark">
                        {monetizeWithDecimal(order.orderFees)}
                    </span>
                    {order.shippingInformation && (
                        <>
                            <span className={'col-span-5'}>Shipping & Handling</span>
                            <span className="text-center text-brand-dark">
                                {monetizeWithDecimal(order.shippingInformation?.cost || 0)}
                            </span>
                        </>
                    )}
                    <ul
                        className={classNames(
                            styles.detailShippingInfo,
                            order.shippingInformation ? 'block' : 'hidden'
                        )}
                    >
                        <li className={classNames(styles.detailShippingLineItems)}>
                            <div className="flex flex-col md:flex-row">
                                <span className="font-semibold">Ship To: </span>
                                {order.shippingInformation?.shippingAddress.address1}
                            </div>
                        </li>
                        <li className={classNames(styles.detailShippingLineItems)}>
                            <div className="flex flex-col md:flex-row">
                                <span className="font-semibold">Shipping Method: </span>
                                {order.shippingInformation?.shippingMethod}
                            </div>
                        </li>
                        <li className={classNames(styles.detailShippingLineItems)}>
                            <div className="flex flex-col md:flex-row">
                                <span className="font-semibold">Tracking Number: </span>
                                <a
                                    href={order.shippingInformation?.trackingUrl}
                                    className={classNames('text-brand cursor-pointer', styles.link)}
                                >
                                    {order.shippingInformation?.trackingNumber}
                                </a>
                            </div>
                        </li>
                    </ul>
                </div>
                <div className={classNames(styles.detailTotalDivider)}>
                    <span className="col-span-3 md:col-span-4" />
                    <span className={classNames(styles.detailTotalLabel)}>Total &nbsp;</span>
                    <span className={classNames(styles.detailTotalAmount)}>
                        {calculateOrderTotal(order)}
                    </span>
                </div>
            </section>
            <section
                onClick={() => setExpand(false)}
                className={classNames(
                    styles.detailFooterClose,
                    styles.link,
                    expand ? 'block' : 'hidden'
                )}
            >
                Close
            </section>
            {giftDetailsModalInfo ? (
                <GiftDetailsModal
                    isOpen={giftDetailsModalInfo !== undefined}
                    onClose={hideGiftDetailsModal}
                    lineItem={giftDetailsModalInfo}
                    logoURL={getBrandSrcUrl(giftDetailsModalInfo?.brandId)}
                />
            ) : null}
        </React.Fragment>
    );
};

interface GiftDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    lineItem: OrderLineItem;
    logoURL: string;
}

const giftDetailsModalStyles = createStyles({
    title: 'text-center font-semibold text-brand-dark text-2xl sm:text-29px',
});

export const GiftDetailsModal = ({ isOpen, onClose, lineItem, logoURL }: GiftDetailsModalProps) => {
    const renderLine = (property: string, value: string) => {
        return (
            <div className="text-grey-medium text-sm sm:text-base flex mt-1">
                <span>{property}</span>
                <span className="ml-auto">{value}</span>
            </div>
        );
    };

    return (
        <Modal
            title=""
            isOpen={isOpen}
            onClose={onClose}
            size={[ModalSize.CONTENT_WIDTH, ModalSize.MEDIUM]}
        >
            <div className={giftDetailsModalStyles.title}>Gift Details</div>

            <div className="flex justify-center mt-4">
                <div className="flex items-center justify-center border border-grey-light inline-block rounded-2xl overflow-hidden w-44 sm:w-48 h-28 sm:h-32">
                    <img src={logoURL} />
                </div>
            </div>

            <div className="mt-9">
                <div className="">
                    <div className="font-semibold">Recipient</div>
                    {renderLine('Name', (lineItem.giftDetails as any)?.toName || '')}
                    {renderLine('Email', (lineItem.giftDetails as any)?.toEmail || '')}
                </div>

                {(lineItem.giftDetails as any)?.fromName ? (
                    <div className="mt-4">
                        <div className="font-semibold">Sender</div>
                        {renderLine('Name', (lineItem.giftDetails as any)?.fromName || '')}
                    </div>
                ) : null}

                {(lineItem.giftDetails as any)?.message ? (
                    <div className="mt-4 ">
                        <div className="font-semibold">Message</div>
                        <div className="text-grey-medium text-base">
                            {(lineItem.giftDetails as any)?.message || ''}
                        </div>
                    </div>
                ) : null}
            </div>

            <div className="text-center mt-8">
                <div className="text-base font-semibold text-grey-medium">{lineItem.brandName}</div>
                <div className=" text-grey-dark font-semibold text-lg sm:text-2xl">{`$${Math.trunc(
                    lineItem.totalValue
                )} eGift Card`}</div>
            </div>

            <div className="text-center mt-6 mb-5">
                <Btn onClick={onClose} isSecondary>
                    <span className="px-12">Close</span>
                </Btn>
            </div>
        </Modal>
    );
};
